import {
  DeliveryCreationType,
  Provider,
  TransactionStatus,
} from '../shared.types';
import {
  DeliveryAssignmentEntity,
  FundedTipScheduleEntity,
} from '../location.types';

export enum vDriveDeliveryStatus {
  pending_assign = 'pending_assign',
  pending_merchant = 'pending_merchant',
  scheduled = 'scheduled',
  runner_assigned = 'runner_assigned',
  en_route_pickup = 'en_route_pickup',
  arrived_pickup = 'arrived_pickup',
  picked_up = 'picked_up',
  en_route_dropoff = 'en_route_dropoff',
  arrived_dropoff = 'arrived_dropoff',
  completed = 'completed',
  cancelled = 'cancelled',
  /** The customer could not be reached and the driver brought the food back. */
  returned = 'returned',
  unknown = 'unknown',
}

export type TransactionsFromDwollaResponse = {
  id: string;
  status: TransactionStatus;
  amount: number;
  createdAt: string;
  bankName: string;
  customerName: string;
};

export type PaymentReportLineEntity = {
  /**
   * The provider's own wording for this line, or one of our deductions.
   * Ready for display: never map or translate it, so a provider renaming
   * one of its lines cannot break the row.
   */
  label: string;
  /** Amount in cents. Positive = added, negative = deducted. */
  amount: number;
  /** When true, render a separator line beneath this row. */
  divider: boolean;
  /**
   * Why this row is on THIS order - the provider's reason for an adjustment,
   * "Paid to the delivery driver". Rendered under the row, smaller.
   */
  note?: string;
  /**
   * What the row IS, the same sentence every time it appears. Rendered
   * behind an info icon next to the label; absent means no icon.
   */
  info?: string;
};

export type PaymentReportIncomeEntity = {
  /**
   * The whole payout for this order as one signed list, rendered top to bottom.
   *
   * A `Received from ...` row is the subtotal of the rows above it rather than
   * another item, so the list only adds up to the last entry (`Your payout`)
   * from that row down. Summing every row double-counts the provider's side.
   */
  components: PaymentReportLineEntity[];
  /**
   * True when the provider paid nothing for this order (report path only).
   * The wording that goes with it is the frontend's.
   */
  nothingReceived: boolean;
};

export type EarningExplanationLineEntity = {
  /** Same label as the matching paymentReportIncome component (1:1 by index). */
  label: string;
  /** Amount in cents. Positive = added, negative = deducted. */
  amount: number;
  /**
   * Why this line is part of the payout, one to three sentences. May be an
   * empty string - render the line without text, never hide it.
   */
  explanation: string;
  /** The provider's own stated reason for the line, when their report had one. */
  note?: string;
};

export type EarningExplanationFaqEntity = {
  question: string;
  answer: string;
};

/**
 * AI-written explanation of one order's payout. Labels and amounts mirror the
 * booked paymentReportIncome.components by index (without the divider flag) -
 * the AI only writes the wording, it never computes an amount.
 */
export type EarningExplanationResponse = {
  orderId: number;
  /** The whole payout story in four to six plain-text sentences. No markdown. */
  summary: string;
  lines: EarningExplanationLineEntity[];
  /** May be empty - hide the FAQ block. */
  faq: EarningExplanationFaqEntity[];
  /** Up to three short notes on what is unusual. May be empty - hide the block. */
  notes: string[];
};

export type vGrubsIncomeEntity = {
  createdAt: string;
  // Null/absent while the earning is still being calculated - the final amount
  // only lands once the provider payment report is processed, which is 3 days
  // after order completion for both Uber Eats and Grubhub. Still never render a
  // fixed wait - use `availableAt` from the API.
  amount?: number | null;
  orderId?: number | null;
  deductionAmount?: number | null;
  grossAmount?: number | null;
  storeName: string;
  type: vOrdersIncomeType;
  status: IncomeStatus;
  reason?: string | null;
  availableAt?: string | null;
  /**
   * The customer paid the restaurant in cash at pickup (partner API orders,
   * e.g. Kanekt365). `amount` is then our fee taken from the location's vGrubs
   * balance, so it is negative - never a payout. False on every other row.
   */
  cashPayment?: boolean;
  /**
   * @deprecated Superseded by `paymentReportIncome`, and null whenever that
   * one is present - the two are the same money in two shapes and the backend
   * only ever sends one. Every earning that has a breakdown now carries it as
   * `paymentReportIncome`, so this is empty in practice and goes away once the
   * transition period ends.
   */
  breakdownIncome?: VOrdersIncomeEntity | null;
  /**
   * The payout breakdown, for all three payout paths: vOrders (and the other
   * vGrubs-own channels), vGrubs/3PD, and a provider payment report. Null
   * while an earning is still waiting for its report - `amount` is null then
   * too, and the row shows as pending instead of as an empty breakdown.
   */
  paymentReportIncome?: PaymentReportIncomeEntity | null;
  /**
   * A Balance Correction a debt repayment plan took from the vGrubs balance:
   * what it was for and how much is left. Null on every other row (the daily
   * vTablet fee included). `reason` already carries the same as a sentence.
   */
  debtRepayment?: VGrubsIncomeDebtRepayment | null;
};

/** Cents. */
export type VGrubsIncomeDebtRepayment = {
  service: 'vDrive' | 'saas';
  /** Taken with this transaction. */
  amount: number;
  /** Owed right before it. */
  debtBefore: number | null;
  /** Left right after it. */
  debtAfter: number | null;
  /** Owed today; 0 = paid off. */
  currentDebt: number;
  /** The debt is paid off and the deductions stopped. */
  paidOff: boolean;
  /**
   * YYYY-MM-DD. vDrive: the delivery days (the location's zone); SaaS: the
   * months the fees were for (UTC).
   */
  periodFrom: string | null;
  periodTo: string | null;
  /** The same sentence as `reason`. */
  message: string;
};

export enum vOrdersIncomeType {
  vGrubs = 'vGrubs',
  vOrders = 'vOrders',
  dcom = 'dcom',
  vGrubsAI = 'vGrubsAI',
  bytes = 'bytes',
  kanekt365 = 'kanekt365',
  balanceIncrementation = 'balanceIncrementation',
  balanceDeduction = 'balanceDeduction',
}

/** @deprecated Read the breakdown from `PaymentReportIncomeEntity` instead. */
export type VOrdersIncomeEntity = {
  dspFee: number;
  vDriveFee: number;
  vGrubsFee: number;
  fundedTip?: number;
  subtotal: number;
  discount: number;
  tax: number;
  tips: number;
  deliveryFee: number;
  platformFee: number;
  amount: number;
  showTipsAsAddition: boolean;
  vDriveDeliveryFee: number;
};

export type vGrubsWithdrawalEntity = {
  createdAt: string;
  amount: number;
  fee: number;
  status: TransactionStatus;
  failureFee?: number;
  description: string;
};

export type vDriveDepositEntity = {
  createdAt: string;
  amount: number;
  stripeFee: number;
  vDriveFee: number;
  vDriveCreditCardFee: number | null;
  amounWithFee: number;
};

export type vDriveTransactionEntity = {
  createdAt: string;
  orderId: number;
  deliveryFee: number;
  deliveryTip: number;
  depositTotal: number;
  networkFee: number;
  vGrubsFee: number;
  totalAmount: number;
  paidCancellationFee: number | null;
  cancellationReason: true | null;
  fundedTip: number | null;
  status: vDriveDeliveryStatus;
  esfcr: number | null;
  savings: number | null;
  provider: Provider;
  creationType: DeliveryCreationType;
  /**
   * Charged with this delivery under the location's debt repayment plan,
   * cents; 0 when nothing was. NOT part of `totalAmount` or `savings`, and
   * not in the row's `amount` either. Missing before Oct 7, 2026.
   */
  debtRepayment?: number;
  /**
   * What `debtRepayment` paid off and what is left, for the info button.
   * Null when nothing was charged or the backend could not build it.
   */
  debtRepaymentInfo?: VDriveDebtRepaymentInfo | null;
};

/** Cents. */
export type VDriveDebtRepaymentInfo = {
  /** All plans together; the same as `debtRepayment`. */
  amount: number;
  /** Every debt this delivery paid toward is paid off. */
  paidOff: boolean;
  /** One per plan, vDrive first, then SaaS. */
  repayments: VGrubsIncomeDebtRepayment[];
  /** The finished text for all plans, lines split by `\n`. */
  message: string;
};

export type NewTransactionResponse<
  T =
    | vGrubsIncomeEntity
    | vGrubsWithdrawalEntity
    | vDriveDepositEntity
    | vDriveTransactionEntity
> = {
  createdAt: string;
  amount: number;
  status: TransactionStatus;
  object: T;
};

export type SumTransactionResponse = {
  vGrubsIncome: number;
  vGrubsWithdrawals: number;
  vDriveDeposits: number;
  vDriveTransactions: number;
  lastVGrubsIncome: number | null;
  lastVGrubsWithdrawal: number | null;
  lastVDriveDeposit: number | null;
  vDriveDepositsReports: number;
};

export type SumTransactionVDriveResponse = {
  vDriveDeposits: number;
  vDriveTransactions: number;
  lastVDriveDeposit: number | null;
  vDriveDepositsReports: number;
  userName: string;
};

export type SumTransactionVGrubsResponse = {
  vGrubsIncome: number;
  vGrubsWithdrawals: number;
  lastVGrubsIncome: number | null;
  lastVGrubsWithdrawal: number | null;
  userName: string;
};

export type AgentSumTransactionsResponse = {
  income: number;
  withdrawals: number;
  lastIncome?: number | null;
  lastWithdrawal?: number | null;
  userName: string;
};

export enum AgentTransactionsType {
  incomes = 'incomes',
  withdrawals = 'withdrawals',
}

export enum AgentIncomeStatus {
  active = 'active',
  pending = 'pending',
  available = 'available',
}

export enum AgentIncomeType {
  vGrubs = 'vGrubs',
  vOrders = 'vOrders',
  vDrive = 'vDrive',
  flatAmount = 'flatAmount',
  pipeline = 'pipeline',
  balanceDeduction = 'balanceDeduction',
  balanceIncrementation = 'balanceIncrementation',
}

export enum IncomeStatus {
  available = 'available',
  pending = 'pending',
}

export enum IncomeType {
  vGrubs = 'vGrubs',
  vOrders = 'vOrders',
  dcom = 'dcom',
  vGrubsAI = 'vGrubsAI',
  bytes = 'bytes',
  kanekt365 = 'kanekt365',
  balanceIncrementation = 'balanceIncrementation',
  balanceDeduction = 'balanceDeduction',
}

export type NewTransactionObject = {
  createdAt: string;
  amount: number;
  type: AgentIncomeType;
  status: AgentIncomeStatus;
  orderId?: number;
  storeName?: string;
  agentName?: string;
  reason?: string;
  availableAt?: string;
  orderVDriveId?: number | null;
};

export type NewTransactionItem = {
  createdAt: string;
  amount: number;
  status?: TransactionStatus | null;
  object: NewTransactionObject;
};

export type VDriveAdminWalletResponse = {
  tipsDifference: number;
  brandsTipsDifference: number;
  vDriveFee: number;
  vGrubsFee: number;
  dspFee: number;
  amount: number;
  totalProfit: number;
};

export type ManageLocationVDriveRequestData = {
  enableVgrubsBrands: boolean;
  autoAssignDriver: boolean;
  exclusiveOfferEnabled: boolean;
  callDispatcherEnabled: boolean;
  callDispatcherMinutes?: number | null;
  merchantId: string;
  providersDisabled?: Array<Provider> | null;
  autoRechargeAmount?: number | null;
  autoRechargeWhenAmount?: number | null;
  achCustomerId?: string | null;
  vDriveFee?: number | null;
  vGrubsFee?: number | null;
  fundedTipSchedule?: FundedTipScheduleEntity[] | null;
  deliveryAssignment?: DeliveryAssignmentEntity | null;
  applyDeliveryAssignenmentToAllBrands?: boolean;
  cwTeamId?: string | null;
  /**
   * Pinned vDrive fleet code. Unlike `cwTeamId`, leaving it out keeps the
   * current one; `null` unpins.
   */
  vdriveOrganizationId?: string | null;
  motoClickEnabled?: boolean | null;
  /**
   * Pickup note handed to the delivery company and shown to the driver.
   * `null` when none is set. Max 280 chars - longer is a 400 from the backend.
   */
  pickupInstructions?: string | null;
};
